/**
 * Access-control TREE — the source of truth for the admin Access page editor.
 *
 * It mirrors the admin UI: SECTION → PAGE → (sub-tab | action button). Each node
 * maps to backend permission code(s); the tree is pure data, the enforcement is
 * the existing `requirePerm` (perms live in the JWT, applied on next sign-in).
 *
 * Node kinds:
 *  - `section`  — a sidebar group; no perm, just a heading with children.
 *  - `page`     — a sidebar leaf/route. `perm` is its nav/view perm; `requires`
 *                 are the EXTRA read perms its load endpoints need, auto-granted
 *                 when the page is enabled (fixes the bug where enabling a page
 *                 granted view.members.users but not the user.read that GET /users
 *                 actually requires).
 *  - `tab`      — a sub-tab on a detail page. `perm` set + `lockable:true` = a real
 *                 toggle (distinct authority). `lockable:false` = informational
 *                 (shares the page's authority, e.g. a slot's genealogy views).
 *  - `button`   — an action. Always lockable; `perm` is the action's authority.
 *
 * The Member-detail and Slot-detail pages are the exception: EVERY tab there is
 * independently lockable (its own `*.detail.*.read` perm) so a role can be scoped
 * down to specific tabs. These detail-tab locks are enforced on the admin frontend
 * (the underlying genealogy/slot endpoints stay member-shared); elsewhere locks are
 * minted only at genuine authority boundaries to keep the catalog + JWT lean.
 */
export type AccessNodeKind = 'section' | 'page' | 'tab' | 'button';

export type AccessNode = {
  key: string;
  label: string;
  kind: AccessNodeKind;
  /** The permission this node grants (page nav perm / tab / button authority). */
  perm?: string;
  /** PAGE nodes only: extra read perms auto-granted when the page is enabled. */
  requires?: string[];
  /** tab/button: false = informational (no toggle, shares the page's authority). */
  lockable?: boolean;
  children?: AccessNode[];
};

// ── builders (keep the literal tree terse + consistent) ────────────────────────
const page = (
  key: string,
  label: string,
  perm: string,
  opts: { requires?: string[]; children?: AccessNode[] } = {},
): AccessNode => ({
  kind: 'page',
  key,
  label,
  perm,
  requires: opts.requires,
  children: opts.children,
});

/** A lockable sub-tab (real backend boundary). */
const tab = (key: string, label: string, perm: string): AccessNode => ({
  kind: 'tab',
  key,
  label,
  perm,
  lockable: true,
});

/** An informational sub-tab — shares the page's authority, no separate toggle. */
const info = (key: string, label: string): AccessNode => ({
  kind: 'tab',
  key,
  label,
  lockable: false,
});

/** A lockable action button. */
const btn = (key: string, label: string, perm: string): AccessNode => ({
  kind: 'button',
  key,
  label,
  perm,
  lockable: true,
});

const section = (key: string, label: string, children: AccessNode[]): AccessNode => ({
  kind: 'section',
  key,
  label,
  children,
});

export const ACCESS_TREE: AccessNode[] = [
  section('members', 'Members', [
    page('members.users', 'Users', 'view.members.users', {
      requires: ['user.read'], // GET /users requires user.read, not the nav perm
      children: [
        tab('members.users.tab.information', 'Information', 'member.detail.info.read'),
        tab('members.users.tab.slots', 'Slots', 'slot.read'),
        tab('members.users.tab.wallet', 'Wallet & Activity', 'member.wallet.read'),
        tab('members.users.tab.verification', 'Verification (KYC)', 'kyc.read'),
        tab('members.users.tab.beneficiary', 'Beneficiary', 'member.detail.beneficiary.read'),
        btn('members.users.btn.new', 'New / edit user', 'user.write'),
        btn('members.users.btn.pwreset', 'Reset password', 'user.password.reset'),
        btn('members.users.btn.adjust', 'Adjust wallet/points', 'member.balance.adjust'),
        btn('members.users.btn.kyc', 'Approve / reject KYC', 'kyc.review'),
      ],
    }),
    page('members.kyc', 'KYC Review', 'view.members.users', {
      requires: ['kyc.read'],
      children: [btn('members.kyc.btn.review', 'Approve / reject', 'kyc.review')],
    }),
    page('members.slots', 'Slots', 'view.members.slots', {
      requires: ['slot.read'], // GET /admin/slots requires slot.read, not the nav perm
      children: [
        tab('members.slots.tab.info', 'Information', 'slot.detail.info.read'),
        tab('members.slots.tab.walletsummary', 'Wallet + Breakdown', 'slot.detail.wallet.read'),
        // Cross-cutting visibility: when off, income figures (Slots-list wallet/
        // earned columns + member-detail summary amounts) are hidden for the role.
        tab('members.slots.tab.income', 'Income amounts (wallet & earned)', 'member.income.view'),
        tab('members.slots.tab.wallet', 'Wallet Logs', 'member.wallet.read'),
        tab('members.slots.tab.distributed', 'Distributed Income', 'distribution.read'),
        tab('members.slots.tab.unilevel', 'Unilevel Tree', 'slot.detail.unilevel.read'),
        tab('members.slots.tab.binary', 'Binary Tree', 'slot.detail.binary.read'),
        tab('members.slots.tab.points', 'Points History', 'slot.detail.points.read'),
        tab('members.slots.tab.inventory', 'Inventory', 'slot.detail.inventory.read'),
        tab('members.slots.tab.binaryhist', 'Binary History', 'slot.detail.binary_history.read'),
        btn('members.slots.btn.new', 'New slot', 'slot.create'),
        btn('members.slots.btn.adjust', 'Adjust wallet/points', 'member.balance.adjust'),
        btn('members.slots.btn.company', 'Mark/unmark company', 'slot.write'),
        btn('members.slots.btn.changeowner', 'Change owner', 'slot.write'),
      ],
    }),
    page('members.memberships', 'Memberships', 'view.members.memberships', {
      // GET /memberships is public (member portal); only writes are gated.
      children: [btn('members.memberships.btn.write', 'Create / edit tiers', 'membership.write')],
    }),
    page('members.access', 'Access (roles)', 'view.members.access', {
      requires: ['user.roles.assign'], // the role builder loads via user.roles.assign
    }),
  ]),

  section('commerce', 'Commerce', [
    page('commerce.orders', 'Orders', 'order.read', {
      children: [
        btn('commerce.orders.btn.status', 'Status / ship / cancel / refund', 'order.write'),
        btn('commerce.orders.btn.payment', 'Approve payment', 'payment.approve'),
      ],
    }),
    page('commerce.products', 'Products & Categories', 'view.products.catalog', {
      children: [
        btn('commerce.products.btn.write', 'Create / edit products & categories', 'product.write'),
      ],
    }),
    page('commerce.coupons', 'Coupons', 'view.products.coupons', {
      children: [btn('commerce.coupons.btn.write', 'Create / edit coupons', 'coupon.write')],
    }),
    page('commerce.suppliers', 'Suppliers', 'view.products.suppliers', {
      children: [btn('commerce.suppliers.btn.write', 'Create / edit suppliers', 'supplier.write')],
    }),
    page('commerce.purchase_orders', 'Purchase Orders', 'view.products.purchase_orders', {
      children: [
        btn('commerce.po.btn.write', 'Create / approve / cancel POs', 'purchase_order.write'),
      ],
    }),
    page('commerce.branches', 'Branches', 'view.branch.branches', {
      children: [
        info('commerce.branches.tab.cashiers', 'Cashiers'),
        info('commerce.branches.tab.inventory', 'Inventory & Logs'),
        btn(
          'commerce.branches.btn.write',
          'Create / edit branch, e-com, numbering, stock',
          'branch.write',
        ),
        btn('commerce.branches.btn.cashiers', 'Provision cashiers', 'cashier.provision'),
      ],
    }),
    page('commerce.shipping', 'Shipping', 'view.branch.shipping', {
      children: [btn('commerce.shipping.btn.write', 'Publish couriers / zones', 'shipping.write')],
    }),
    // ONE page node covering the three payment-config sidebar leaves (POS
    // payment methods, POS custom fees, Ecom payment methods) — they share a
    // view perm and one write authority (`tenant.payment_methods.write`, the
    // key-scoped features-PATCH slice mirroring `tenant.storefront.write`).
    page('commerce.payment_methods', 'Payment methods & POS fees', 'view.commerce.payment_methods', {
      children: [
        btn(
          'commerce.payment_methods.btn.write',
          'Edit payment methods & POS fees',
          'tenant.payment_methods.write',
        ),
      ],
    }),
    page('commerce.merchants', 'Merchants', 'view.merchants.accounts', {
      children: [
        info('commerce.merchants.tab.accounts', 'Accounts'),
        tab('commerce.merchants.tab.products', 'Products', 'view.merchants.products'),
        tab('commerce.merchants.tab.payouts', 'Payouts', 'view.merchants.payouts'),
        btn('commerce.merchants.btn.account', 'Approve / suspend account', 'merchant.approve'),
        btn(
          'commerce.merchants.btn.product',
          'Approve / reject product',
          'merchant.product.approve',
        ),
        btn('commerce.merchants.btn.payout', 'Process merchant payouts', 'merchant.payout.process'),
      ],
    }),
  ]),

  /**
   * Cashier POS — the `pos.*` perms. These were Advanced-only for a long time,
   * which meant a tenant could not grant/revoke "may this cashier hand out a
   * free slot?" without hand-picking a raw permission code. They gate the
   * cashier SPA's nav + action buttons, not admin pages, but they are real
   * authority boundaries and belong in the editor like any other.
   */
  section('cashier', 'Cashier POS', [
    page('cashier.checkout', 'Checkout & Sales Orders', 'pos.checkout', {
      children: [
        btn('cashier.checkout.btn.fs', 'Activate free slot (FS)', 'pos.slot.activate'),
        btn('cashier.checkout.btn.refund', 'Void / refund a sale', 'pos.refund'),
        btn(
          'cashier.checkout.btn.memo',
          'Edit remarks after the fact (sale remarks, void/refund reason, shift notes)',
          'pos.memo.edit',
        ),
        btn(
          'cashier.checkout.btn.discount',
          'Approve above-cap discount (manager PIN)',
          'pos.discount.override',
        ),
      ],
    }),
    page('cashier.releasing', 'Releasing', 'pos.release'),
    page('cashier.orders', 'Orders', 'pos.orders.view', {
      children: [
        btn(
          'cashier.orders.btn.fulfill',
          'Advance fulfilment (ship / deliver / complete)',
          'pos.orders.fulfill',
        ),
      ],
    }),
    // Read-only: branch stock levels + the append-only movement log ("View
    // log"). Separate from admin's branch.write, which also grants receive/
    // write-off.
    page('cashier.inventory', 'Inventory & Logs', 'pos.inventory.view'),
  ]),

  section('messages', 'Messages & Feedback', [
    page('comms.messages', 'Messages', 'view.messages'),
    page('comms.feedback', 'Feedback', 'view.feedback'),
  ]),

  section('finance', 'Finance', [
    page('finance.payments', 'Payments', 'view.payments', {
      requires: ['payment.approve'], // the requests list loads via payment.approve
      children: [
        btn('finance.payments.btn.review', 'Verify / approve / reject', 'payment.approve'),
      ],
    }),
    page('finance.payouts', 'Payouts', 'view.payouts.requests', {
      requires: ['payout.approve'],
      children: [
        tab('finance.payouts.tab.batches', 'Batches', 'payout.process'),
        btn('finance.payouts.btn.process', 'Batch / mark paid / reject', 'payout.process'),
      ],
    }),
    page('finance.fees', 'Fees & Tax', 'view.payouts.fees', {
      children: [
        btn('finance.fees.btn.write', 'Edit methods & deductions', 'tenant.features.write'),
      ],
    }),
  ]),

  section('compensation', 'Compensation', [
    page('comp.plans', 'Plans', 'view.compensation.compplan', {
      children: [btn('comp.plans.btn.publish', 'Publish config', 'compplan.config.write')],
    }),
    page('comp.periods', 'Periods', 'view.compensation.periods', {
      children: [btn('comp.periods.btn.close', 'Close period', 'compplan.recompute')],
    }),
    page('comp.distribute', 'Distribution', 'view.compensation.distribute', {
      children: [
        btn(
          'comp.distribute.btn.run',
          'Activate / load / compute / distribute',
          'compplan.recompute',
        ),
      ],
    }),
    page('comp.distributions', 'Distributions', 'view.compensation.distributions', {
      requires: ['distribution.read'],
    }),
    page('comp.vortex', 'Vortex Monitor', 'view.compensation.vortex'),
  ]),

  section('storefront', 'Storefront', [
    page('storefront.content', 'Content Editor', 'view.settings.tenant', {
      // Storefront-CONTENT saves go through the grantable `tenant.storefront.write`
      // (a scoped slice of `tenant.features.write` — a features PATCH touching Fees
      // & Tax / settings / comp flags still needs the full super-admin-only perm).
      children: [
        btn('storefront.content.btn.write', 'Save storefront content', 'tenant.storefront.write'),
      ],
    }),
    page('storefront.landing_packages', 'Landing Packages', 'view.settings.tenant', {
      children: [
        btn(
          'storefront.landing_packages.btn.write',
          'Save landing packages',
          'tenant.storefront.write',
        ),
      ],
    }),
    page('storefront.featured_packages', 'Featured Packages', 'view.settings.tenant', {
      children: [
        btn(
          'storefront.featured_packages.btn.write',
          'Save featured packages',
          'tenant.storefront.write',
        ),
      ],
    }),
    page('storefront.feedback', 'Official Store Feedback', 'view.products.catalog', {
      children: [
        btn('storefront.feedback.btn.feature', 'Feature / unfeature review', 'review.feature'),
      ],
    }),
  ]),

  section('content', 'Content', [
    page('content.videos', 'Training Videos', 'view.assets.training_videos', {
      children: [
        btn('content.videos.btn.write', 'Create / edit videos & categories', 'asset.write'),
      ],
    }),
    page('content.resources', 'Resources', 'view.assets.resources', {
      children: [
        btn('content.resources.btn.write', 'Create / edit resources & categories', 'asset.write'),
      ],
    }),
    // The tenant's PUBLIC landing pages. Previously this rode
    // `view.assets.training_videos` + `asset.write`, which meant granting
    // someone Training Videos silently handed them the landing-page builder.
    // Its own view perm + its own narrow write button now.
    page('content.replicated', 'Replicated Pages', 'view.content.replicated', {
      children: [
        btn('content.replicated.btn.write', 'Create / edit pages', 'content.replicated.write'),
      ],
    }),
    page('content.announcements', 'Announcements', 'view.announcements', {
      children: [
        btn('content.announcements.btn.write', 'Create / edit announcements', 'announcement.write'),
      ],
    }),
  ]),

  section('reports', 'Reports', [
    page('reports.page', 'Reports', 'view.reports', { requires: ['report.read'] }),
  ]),

  section('settings', 'Settings', [
    page('settings.system', 'System', 'view.settings.tenant', {
      // The 19 settings cards all save through one lock (tenant.features.write,
      // super-admin-only) — kept as a single authority per the product decision.
      children: [btn('settings.system.btn.write', 'Save settings', 'tenant.features.write')],
    }),
    page('settings.audit', 'Audit log', 'view.settings.audit', { requires: ['audit.read'] }),
    page('settings.dlq', 'Failed jobs (DLQ)', 'view.payouts.dlq', {
      requires: ['dlq.manage'],
      children: [btn('settings.dlq.btn.manage', 'Retry / drop jobs', 'dlq.manage')],
    }),
  ]),
];

// ── derived helpers ────────────────────────────────────────────────────────────

function walk(nodes: AccessNode[], visit: (n: AccessNode) => void): void {
  for (const n of nodes) {
    visit(n);
    if (n.children) walk(n.children, visit);
  }
}

/** Every permission referenced anywhere in the tree (perm + requires), de-duped. */
export const TREE_PERMS: string[] = (() => {
  const set = new Set<string>();
  walk(ACCESS_TREE, (n) => {
    if (n.perm) set.add(n.perm);
    for (const r of n.requires ?? []) set.add(r);
  });
  return Array.from(set);
})();

const TREE_PERM_SET = new Set(TREE_PERMS);

/** True if a permission code is surfaced by the tree (vs. the Advanced list). */
export function isTreePerm(code: string): boolean {
  return TREE_PERM_SET.has(code);
}

/**
 * Map of page-view perm → the extra read perms it auto-grants when enabled.
 * Used CLIENT-side (expand before the PUT) AND SERVER-side (re-expand in the
 * access route) so a page can never be granted without its read perms.
 */
export const PAGE_REQUIRES: Record<string, string[]> = (() => {
  const out: Record<string, string[]> = {};
  walk(ACCESS_TREE, (n) => {
    if (n.kind === 'page' && n.perm && n.requires?.length) {
      out[n.perm] = Array.from(new Set([...(out[n.perm] ?? []), ...n.requires]));
    }
  });
  return out;
})();

/**
 * Expand a set of permission codes so that whenever a page-view perm is present,
 * its `requires[]` read perms are too. Pure; returns a new sorted array.
 */
export function expandPageRequires(codes: Iterable<string>): string[] {
  const set = new Set(codes);
  for (const code of Array.from(set)) {
    for (const req of PAGE_REQUIRES[code] ?? []) set.add(req);
  }
  return Array.from(set).sort();
}

/**
 * Roles a tenant admin (non-super-admin) must never assign to a user — doing so
 * would mint another platform/tenant administrator. Mirrored server-side in
 * apps/api/src/routes/access.ts.
 */
export const PLATFORM_TIER_ROLE_CODES = ['super_admin', 'admin'] as const;

export function isPlatformTierRole(code: string): boolean {
  return (PLATFORM_TIER_ROLE_CODES as readonly string[]).includes(code);
}
